// File: frontend/src/components/common/ResponsiveStatsTable.tsx
//
// Shared, best-practice responsive stats table — one component the whole app's
// data surfaces (player pools, leaderboards, etc.) render through, so the mobile
// UX and accessibility live in ONE place instead of ~10 hand-rolled <table>s.
//
// Best practices baked in (NN/g, Smashing, W3C APG, Adrian Roselli):
//  - Frozen first column with a SOLID opaque background + edge shadow, so the
//    scrolling columns pass cleanly underneath it (no bleed-through), plus a
//    sticky header and a right-edge scroll cue.
//  - Accessible sorting: headers are real <button>s, keyboard-operable, with
//    `aria-sort` on the currently-sorted column.
//  - SIGNATURE mobile layout: rather than swiping through a wall of raw columns,
//    stat columns are PAGED BY GROUP (e.g. Scoring | Creating | Progression |
//    More) via a segmented control. Identity/headline columns stay pinned;
//    desktop shows every column.
//  - Opt-in ROW WINDOWING (`virtualize`): only the visible slice mounts, so a
//    full-season pool (~700 rows) scrolls/sorts smoothly. Dependency-free —
//    fixed row height + spacer rows holding the scroll height.
//  - Standardized loading / empty states.

import React, { useCallback, useEffect, useRef, useState } from 'react';

export type ColAlign = 'left' | 'right' | 'center';

export interface StatColumn<T> {
  key: string;
  header: string;
  /** Hover tooltip (desktop) explaining an abbreviation. */
  tooltip?: string;
  align?: ColAlign;
  /** Mobile group bucket. Columns whose group === `idGroup` are always visible;
   *  others appear only when their group is the active mobile tab (always shown
   *  on desktop). */
  group?: string;
  /** Mobile tabs this column belongs to — multi-membership (e.g. a column that
   *  appears in both its own tab and an "All" superset tab). When present, this
   *  overrides `group` for mobile visibility. */
  groups?: string[];
  sortable?: boolean;
  /** Frozen (sticky-left) identity column. Multiple columns use leftOffsetPx. */
  frozen?: boolean;
  leftOffsetPx?: number;
  frozenShadow?: boolean;
  /** Optional sticky-right action column. Multiple action columns set explicit
   * right offsets so research stats can scroll beneath persistent actions. */
  frozenRight?: boolean;
  rightOffsetPx?: number;
  widthPx?: number;
  /** Custom cell renderer; falls back to accessor/format/String(value). */
  cell?: (row: T) => React.ReactNode;
  accessor?: (row: T) => unknown;
  format?: (value: unknown) => string;
  /** Extra cell classes (e.g. emphasis on the headline stat). */
  cellClassName?: string;
  headerClassName?: string;
  /** Min width for the (frozen) column. */
  minWidthPx?: number;
}

export interface MobileGroup { key: string; label: string; }

interface Props<T> {
  columns: StatColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (key: string) => void;
  onRowClick?: (row: T) => void;
  highlightRowKey?: string | number | null;
  isLoading?: boolean;
  emptyMessage?: React.ReactNode;
  /** Ordered mobile column-group tabs. When provided, mobile pages by group. */
  mobileGroups?: MobileGroup[];
  mobileGroupVariant?: 'pills' | 'tabs';
  mobileGroupLeading?: React.ReactNode;
  /** Group whose columns are always visible on mobile. Default 'id'. */
  idGroup?: string;
  initialMobileGroup?: string;
  ariaLabel?: string;
  /** Opt in to row windowing (only the visible slice renders). The component
   *  bounds its own scroll height when enabled so the windowing has a viewport. */
  virtualize?: boolean;
  /** Fixed virtual row height. Override when a custom identity cell uses two
   *  lines instead of the shared table's normal one-line row. */
  virtualRowHeightPx?: number;
  /** Infinite-scroll hook. Called before the user reaches the loaded tail so
   *  the next page can arrive without an explicit Load More interaction. */
  onEndReached?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  endReachedThresholdPx?: number;
}

const FROZEN_SHADOW = 'shadow-[6px_0_6px_-4px_rgba(0,0,0,0.55)]';
const FROZEN_RIGHT_SHADOW = 'shadow-[-6px_0_6px_-4px_rgba(0,0,0,0.55)]';
// Windowing uses a fixed caller-supplied row height so only the visible slice
// mounts. The default covers the shared one-line table row.
const VIRT_ROW_H = 44;
const VIRT_OVERSCAN = 10;

function defaultFormat(v: unknown): string {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'number') return Number.isNaN(v) ? '-' : String(v);
  return String(v);
}

export function ResponsiveStatsTable<T>({
  columns,
  rows,
  rowKey,
  sortBy,
  sortOrder,
  onSortChange,
  onRowClick,
  highlightRowKey,
  isLoading,
  emptyMessage,
  mobileGroups,
  mobileGroupVariant = 'pills',
  mobileGroupLeading,
  idGroup = 'id',
  initialMobileGroup,
  ariaLabel = 'Statistics table — scroll horizontally to see more',
  virtualize = false,
  virtualRowHeightPx = VIRT_ROW_H,
  onEndReached,
  hasMore = false,
  isLoadingMore = false,
  endReachedThresholdPx,
}: Props<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  const endRequestRowCountRef = useRef<number | null>(null);
  const [showRightFade, setShowRightFade] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(600);
  const firstGroup = mobileGroups?.[0]?.key ?? null;
  const [activeGroup, setActiveGroup] = useState<string | null>(initialMobileGroup ?? firstGroup);

  // Right-edge cue (always) + scroll position for windowing (only when enabled).
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    if (virtualize) { setScrollTop(el.scrollTop); setViewportH(el.clientHeight); }
    const resolvedEndThreshold = endReachedThresholdPx ?? virtualRowHeightPx * 12;
    const nearLoadedEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - resolvedEndThreshold;
    if (
      nearLoadedEnd && onEndReached && hasMore && !isLoadingMore
      && endRequestRowCountRef.current !== rows.length
    ) {
      endRequestRowCountRef.current = rows.length;
      onEndReached();
    }
  }, [virtualize, virtualRowHeightPx, endReachedThresholdPx, onEndReached, hasMore, isLoadingMore, rows.length]);
  useEffect(() => { handleScroll(); }, [rows, activeGroup, handleScroll]);
  useEffect(() => {
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (highlightRowKey != null && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [highlightRowKey, rows]);

  // Mobile visibility: hidden on mobile unless it's an id column or in the active
  // group; always shown on desktop (sm+). No-op when mobileGroups isn't provided.
  const mobileClass = useCallback((col: StatColumn<T>): string => {
    if (!mobileGroups) return '';
    // A column can belong to several mobile tabs (e.g. an "All" superset). It's
    // visible on mobile if it's an identity column or the active tab is one of
    // its groups; always visible on desktop.
    const groups = col.groups ?? [col.group ?? idGroup];
    if (groups.includes(idGroup) || (activeGroup != null && groups.includes(activeGroup))) return '';
    return 'hidden sm:table-cell';
  }, [mobileGroups, activeGroup, idGroup]);

  // aria-sort only on the currently-sorted column (per MDN / W3C APG).
  const ariaSortFor = (col: StatColumn<T>): React.AriaAttributes['aria-sort'] => {
    if (!col.sortable || sortBy !== col.key) return undefined;
    return sortOrder === 'asc' ? 'ascending' : 'descending';
  };

  // Windowing: mount only the visible slice (+ overscan) and hold the rest of the
  // scroll height with spacer rows. Falls back to all rows when disabled.
  // `effTop` is clamped so shrinking the list (e.g. a filter) can't leave the
  // window scrolled past the end and render a blank slice.
  const total = rows.length;
  const maxScroll = Math.max(0, total * virtualRowHeightPx - viewportH);
  const effTop = virtualize ? Math.min(scrollTop, maxScroll) : 0;
  const winStart = virtualize ? Math.max(0, Math.floor(effTop / virtualRowHeightPx) - VIRT_OVERSCAN) : 0;
  const winEnd = virtualize ? Math.min(total, Math.ceil((effTop + viewportH) / virtualRowHeightPx) + VIRT_OVERSCAN) : total;
  const padTop = winStart * virtualRowHeightPx;
  const padBottom = Math.max(0, (total - winEnd) * virtualRowHeightPx);
  const renderRows = virtualize ? rows.slice(winStart, winEnd) : rows;

  if (isLoading && rows.length === 0) {
    return <p className="text-center text-gray-400 italic p-4">Loading…</p>;
  }
  if (rows.length === 0) {
    return <div className="text-center text-gray-500 italic p-6">{emptyMessage ?? 'No data available.'}</div>;
  }

  return (
    <div className="relative flex flex-col h-full">
      {mobileGroups && mobileGroups.length > 0 && (
        <div
          className={`sm:hidden flex overflow-x-auto bg-primary-dark border-b border-gray-700 scrollbar-mobile-hide ${mobileGroupVariant === 'tabs' ? 'h-9 items-stretch gap-0.5 px-1' : 'gap-1 p-2'}`}
          role="tablist"
          aria-label="Stat groups"
        >
          {mobileGroupLeading}
          {mobileGroups.map(g => {
            const active = g.key === activeGroup;
            return (
              <button
                key={g.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setActiveGroup(g.key);
                  if (scrollRef.current) scrollRef.current.scrollLeft = 0;
                }}
                className={mobileGroupVariant === 'tabs'
                  ? `min-w-0 flex-1 whitespace-nowrap border-b-2 px-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors ${active ? 'border-accent-yellow bg-accent-yellow/10 text-accent-yellow' : 'border-transparent text-gray-400 hover:text-gray-200'}`
                  : `min-h-9 min-w-[44px] flex-none whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition-colors min-[375px]:text-xs ${active ? 'border-accent-yellow bg-accent-yellow text-primary' : 'border-accent-yellow/60 bg-primary text-gray-300 hover:border-accent-yellow hover:text-white'}`
                }
              >
                {g.label}
              </button>
            );
          })}
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
        className={`flex-grow overflow-x-auto overflow-y-auto ${virtualize ? 'max-h-[72vh]' : ''}`}
      >
        {/* text-gray-200 on the table itself: cells inherit color, and a
            consumer page without a light text color on its own container
            otherwise renders near-black stats on the dark theme (bit the
            league Players page). The table owns its legibility. */}
        <table className="w-full text-left text-xs sm:text-sm text-gray-200 tabular-nums">
          <thead className="sticky top-0 bg-primary z-20">
            <tr>
              {columns.map(col => {
                const isSortable = !!col.sortable && !!onSortChange;
                const isActive = sortBy === col.key;
                const alignClass = `text-${col.align ?? 'left'}`;
                let th = `p-2 font-semibold uppercase tracking-wider whitespace-nowrap ${alignClass} select-none ${mobileClass(col)}`;
                th += isActive ? ' text-accent-yellow' : ' text-gray-400';
                if (col.frozen) th += ` sticky top-0 bg-primary z-30 ${col.frozenShadow === false ? '' : FROZEN_SHADOW}`;
                if (col.frozenRight) th += ` sticky top-0 bg-primary z-30 ${FROZEN_RIGHT_SHADOW}`;
                if (col.tooltip) th += ' relative group/header';
                if (col.headerClassName) th += ` ${col.headerClassName}`;
                const columnStyle: React.CSSProperties = {
                  ...(col.frozen ? { minWidth: `${col.minWidthPx ?? 140}px` } : {}),
                  ...(col.widthPx ? {
                    width: `${col.widthPx}px`,
                    minWidth: `${col.widthPx}px`,
                    maxWidth: `${col.widthPx}px`,
                  } : {}),
                  ...(col.frozenRight ? { right: `${col.rightOffsetPx ?? 0}px` } : {}),
                  ...(col.frozen ? { left: `${col.leftOffsetPx ?? 0}px` } : {}),
                };
                return (
                  <th key={col.key} scope="col" aria-sort={ariaSortFor(col)} className={th} style={columnStyle}>
                    {isSortable ? (
                      <button
                        type="button"
                        onClick={() => onSortChange!(col.key)}
                        className={`inline-flex items-center gap-1 ${col.align === 'right' ? 'flex-row-reverse' : ''} cursor-pointer hover:text-gray-200 focus:outline-none focus-visible:underline`}
                      >
                        <span>{col.header}</span>
                        {isActive && <span className="text-badge">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                      </button>
                    ) : (
                      <span>{col.header}</span>
                    )}
                    {col.tooltip && (
                      // Below the header (top-full), NOT above: the thead sits at
                      // the top edge of an overflow scroll container, so an
                      // above-the-header tooltip renders outside the clip box
                      // and never becomes visible (caught 2026-07-20 - hover
                      // "worked" but nothing appeared on any stats table).
                      <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-gray-900 text-gray-200 text-xs font-normal normal-case tracking-normal rounded shadow-lg border border-gray-600 whitespace-nowrap opacity-0 group-hover/header:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
                        {col.tooltip}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {padTop > 0 && (
              <tr aria-hidden="true"><td colSpan={columns.length} style={{ height: padTop, padding: 0, border: 0 }} /></tr>
            )}
            {renderRows.map(row => {
              const key = rowKey(row);
              const highlighted = highlightRowKey != null && key === highlightRowKey;
              const clickable = !!onRowClick;
              return (
                <tr
                  key={key}
                  ref={highlighted ? highlightedRowRef : null}
                  style={virtualize ? { height: virtualRowHeightPx } : undefined}
                  className={`${highlighted ? 'bg-surface-raised ring-1 ring-accent-yellow' : ''} ${
                    clickable ? 'cursor-pointer hover:bg-primary' : 'hover:bg-primary'
                  } transition-colors duration-150`}
                  onClick={clickable ? () => onRowClick!(row) : undefined}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick!(row); }
                  } : undefined}
                >
                  {columns.map(col => {
                    const alignClass = `text-${col.align ?? 'left'}`;
                    let td = `p-2 truncate ${alignClass} ${mobileClass(col)}`;
                    if (col.frozen) td += ` sticky z-10 ${highlighted ? 'bg-surface-raised' : 'bg-primary'} ${col.frozenShadow === false ? '' : FROZEN_SHADOW} font-semibold`;
                    if (col.frozenRight) td += ` sticky z-20 ${highlighted ? 'bg-surface-raised' : 'bg-primary'} ${FROZEN_RIGHT_SHADOW}`;
                    if (col.cellClassName) td += ` ${col.cellClassName}`;
                    // The sorted column's values go gold (its header is already
                    // gold); every other column stays the default near-white.
                    if (sortBy === col.key) td += ' text-accent-yellow';
                    let content: React.ReactNode;
                    if (col.cell) content = col.cell(row);
                    else {
                      const raw = col.accessor ? col.accessor(row) : (row as Record<string, unknown>)[col.key];
                      content = col.format ? col.format(raw) : defaultFormat(raw);
                    }
                    const cellStyle: React.CSSProperties = {
                      ...(col.widthPx ? {
                        width: `${col.widthPx}px`,
                        minWidth: `${col.widthPx}px`,
                        maxWidth: `${col.widthPx}px`,
                      } : {}),
                      ...(col.frozenRight ? { right: `${col.rightOffsetPx ?? 0}px` } : {}),
                      ...(col.frozen ? { left: `${col.leftOffsetPx ?? 0}px` } : {}),
                    };
                    return <td key={col.key} className={td} style={cellStyle}>{content}</td>;
                  })}
                </tr>
              );
            })}
            {padBottom > 0 && (
              <tr aria-hidden="true"><td colSpan={columns.length} style={{ height: padBottom, padding: 0, border: 0 }} /></tr>
            )}
            {isLoadingMore && (
              <tr aria-live="polite">
                <td colSpan={columns.length} className="p-3 text-center text-sm text-gray-400">
                  Loading more…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showRightFade && (
        <div
          className={`pointer-events-none absolute bottom-0 z-[15] w-6 bg-gradient-to-l from-primary-dark to-transparent ${mobileGroups ? 'top-9 sm:top-0' : 'top-0'}`}
          style={{
            right: `${columns.reduce((max, column) => column.frozenRight ? Math.max(max, (column.rightOffsetPx ?? 0) + (column.widthPx ?? 0)) : max, 0)}px`,
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default ResponsiveStatsTable;
